import { useQuery } from "@tanstack/react-query";
import type { Address } from "viem";
import type { RedKeel } from "../config/networks";

/**
 * QUÉ ESTÁ ESPERANDO EL ROBOT, Y POR QUÉ.
 *
 * Una piscina fuera de rango y un robot que no hace nada se ven igual estén bien o estén rotos. Y la
 * diferencia importa: si el robot está aplazando el reajuste a propósito —porque el gas de la red
 * está caro y esperar veinte minutos ahorra dos dólares— el dueño tiene que poder leerlo, no
 * quedarse mirando un "fuera de rango" en rojo preguntándose si el operador se ha caído.
 *
 * El keeper publica en cada ronda lo que está esperando; aquí se lee y se enseña.
 *
 * SI EL FICHERO SE QUEDA VIEJO, NO SE CUENTA NADA. Un keeper parado dejaría su último estado
 * congelado, y enseñarlo sería peor que no decir nada: la app estaría afirmando que espera por el
 * gas cuando en realidad no hay nadie esperando. Por eso se descarta pasados unos minutos.
 */
export type EsperaKeeper = {
  motivo: "gas";
  /** base fee actual de la red, en gwei */
  gwei: number;
  /** por debajo de esto reajusta */
  umbral: number;
  /** cuánto lleva esperando, en segundos */
  esperando_s: number;
  /** cuánto puede esperar como mucho antes de reajustar igual */
  tope_s: number;
};

const FRESCO_MS = 15 * 60 * 1000;

export function useEsperaKeeper(red: RedKeel, vault?: Address) {
  return useQuery({
    queryKey: ["espera-keeper", red.clave, vault],
    enabled: !!vault,
    refetchInterval: 60_000,
    queryFn: async (): Promise<EsperaKeeper | null> => {
      const r = await fetch(`/keeper.${red.clave}.json?t=${Date.now()}`);
      if (!r.ok) return null; // la red no publica estado: no se enseña nada
      const d = (await r.json()) as { actualizado: number; vaults: Record<string, EsperaKeeper> };
      if (!d?.actualizado || Date.now() - d.actualizado * 1000 > FRESCO_MS) return null;
      return d.vaults?.[vault!.toLowerCase()] ?? null;
    },
  });
}
